import { PlanProps } from "@/types/plan";
import { useCallback, useContext, useMemo, useState, createContext } from "react";
import {
  GTM_EVENT,
  GTM_EVENT_NAME,
  ItemEventData,
  ViewItemListEventData,
} from "@/internals/constants/events";
import { UserContext } from "../UserContext";

export const DatalayerContext = createContext(undefined);

export const DatalayerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedPlan, setSelectedPlan] = useState<PlanProps | null>(null);
  const userContext = useContext(UserContext);

  const buildPlanItem = (plan: PlanProps) => {
    const item = {
      item_name: plan.name,
      item_category: "Recurring",
      price: plan.price,
      quantity: 1,
    };
    return [item];
  };

  const buildEventItem = (plan: PlanProps) => {
    const chargedPlanValue = plan.hasTrial ? plan.trialPrice : plan.price;
    const item = {
      value: chargedPlanValue,
      currency: plan.currency,
      items: buildPlanItem(plan),
      userEmail: userContext.user.email,
      "user-id": userContext.user.userId,
    };

    return item;
  };

  const pushEventViewItemList = useCallback(
    (plans: PlanProps[]) => {
      if (!userContext?.user || !plans.length) return;

      const transformedPlansForViewList = plans.map((item) => {
        const itemPrice = item.hasTrial ? item.trialPrice : item.price;
        return {
          item_name: item.name,
          price: itemPrice,
          recurrence_price: item.price,
          quantity: 1,
        };
      });

      window.dataLayer?.push({
        event: GTM_EVENT_NAME.VIEW_ITEM_LIST,
        view_item_list: {
          currency: plans[0].currency,
          items: transformedPlansForViewList,
          userEmail: userContext.user.email,
          "user-id": userContext.user.userId,
        } as ViewItemListEventData,
      } as GTM_EVENT);
    },
    [userContext]
  );

  const pushEventAddToCart = useCallback(
    (plan: PlanProps) => {
      if (!userContext?.user || !plan) return;
      setSelectedPlan(plan);

      window.dataLayer?.push({
        event: GTM_EVENT_NAME.ADD_TO_CART,
        add_to_cart: buildEventItem(plan) as ItemEventData,
      } as GTM_EVENT);
    },
    [userContext]
  );

  const pushEventBeginCheckout = useCallback(() => {
    if (!selectedPlan || !userContext?.user) return;

    window.dataLayer?.push({
      event: GTM_EVENT_NAME.BEGIN_CHECKOUT,
      begin_checkout: buildEventItem(selectedPlan) as ItemEventData,
    } as GTM_EVENT);
  }, [userContext]);

  const pushEventAddShippingInfo = useCallback(() => {
    if (!selectedPlan || !userContext?.user) return;

    window.dataLayer?.push({
      event: GTM_EVENT_NAME.ADD_SHIPPING_INFO,
      add_shipping_info: buildEventItem(selectedPlan) as ItemEventData,
    } as GTM_EVENT);
  }, [selectedPlan, userContext]);

  const pushEventAddPaymentInfo = useCallback(() => {
    if (!selectedPlan || !userContext?.user) return;

    window.dataLayer?.push({
      event: GTM_EVENT_NAME.ADD_PAYMENT_INFO,
      add_payment_info: buildEventItem(selectedPlan) as ItemEventData,
    } as GTM_EVENT);
  }, [selectedPlan, userContext]);

  const pushEventUnsubscribe = useCallback(() => {
    if (!userContext?.user) return;

    const userPlan = userContext.user.subscription?.plan;
    const userData = userContext.user;
    const userTransactionData = userContext.userTransactionData?.items.find(
      (item) => item.subscriptionId === userData.subscription?.id && item.isFirstPayment
    );

    window.dataLayer?.push({
      event: GTM_EVENT_NAME.UNSUBSCRIBE,
      unsubscribe: {
        transaction_id: userTransactionData?.orderId || "",
        value: userTransactionData?.amount || "",
        currency: userTransactionData?.currency || "USD",
        items: buildPlanItem(userPlan),
        userEmail: userData.email,
        "user-id": userData.userId,
      },
    } as GTM_EVENT);
  }, [userContext]);

  const value = useMemo(
    () => ({
      selectedPlan,
      pushEventViewItemList,
      pushEventAddToCart,
      pushEventAddShippingInfo,
      pushEventAddPaymentInfo,
      pushEventBeginCheckout,
      pushEventUnsubscribe,
    }),
    [
      selectedPlan,
      pushEventViewItemList,
      pushEventAddToCart,
      pushEventBeginCheckout,
      pushEventAddShippingInfo,
      pushEventAddPaymentInfo,
      pushEventUnsubscribe,
    ]
  );

  return <DatalayerContext.Provider value={value}>{children}</DatalayerContext.Provider>;
};
